import { faSparkles } from '@fortawesome/pro-duotone-svg-icons';
import { Badge, Tag } from 'antd';
import PropTypes from 'prop-types';
import { matchPath } from 'react-router';
import { Navigate } from 'src/helpers';
import { DuotoneAnimatedIcon } from 'src/helpers/Animations';
import { usePreferences } from 'src/hooks';
import { t } from 'src/i18n/i18next';
import { isPastRetirement } from 'src/components/commons/sunset';
import RedirectToSunset from 'src/routing/RedirectToSunset';

export function isBackboneLoaded() {
  const { app } = window;
  return !!(app && app.security && app.security.hasPermission && app.security.staticPermissions && !app.security.placeholder);
}

export function hasAccessTo(route, hasRuntimePermission) {

  if (route.hideIn) {
    if (route.hideIn.split(',').some(p => p === window.location.hostname.split('.')[0])) {
      return false;
    }
  }

  const { app, _ } = window; // from backbone

  if (route.requireRule) {
    if (!route.requireRule()) {
      return false;
    }
  }

  if (route.requirePreference) {
    if (_.isArray(route.requirePreference)) {
      for (const preference of route.requirePreference) {
        if (!app.prefs.getBoolean(preference)) {
          return false;
        }
      }
    } else if (_.isObject(route.requirePreference)) {
      if (!app.prefs.getBoolean(route.requirePreference.key, route.requirePreference.defaultValue, route.requirePreference.scope)) {
        return false;
      }
    } else {
      if (!app.prefs.getBoolean(route.requirePreference)) {
        return false;
      }
    }
  }

  if (route.requirePermission) {
    if (hasRuntimePermission) {
      return true;
    }
    if (typeof route.requirePermission === 'function') {
      const checkSubRoutes = () => (route.routes || []).filter((subRoute) => !subRoute.isCollapsedTitle).some((subRoute) => hasAccessTo(subRoute, hasRuntimePermission));
      if (!route.requirePermission(app.security, checkSubRoutes)) {
        return false;
      }
    } else {

      const requirePermissions = route.requirePermission.split(',');
      if (!requirePermissions.some(p => app.security.hasPermission(p))) {

        if (!route.dynamicPermission) {
          return false;
        }

        const dynamicPermissions = route.dynamicPermission.split(',');
        if (!dynamicPermissions.some(p => app.security.hasDynamicPermission(p))) {
          return false;
        }
      }
    }
  }

  return true;
}

export function filterRoutes(routes = [], matchParams, hasRuntimePermission, routesOrderPreference) {
  let filteredRoutes = [];

  for (const route of routes) {
    if (hasAccessTo(route, hasRuntimePermission)) {
      if (hasRuntimePermission && !hasRuntimePermission(route.requirePermission, matchParams)) {
        continue;
      }
      const filtered = Object.assign({}, route);
      if (filtered.routes) {
        filtered.routes = filterRoutes(filtered.routes, matchParams, route.hasRuntimePermission || hasRuntimePermission, filtered.routesOrderPreference);
      }
      filteredRoutes.push(filtered);
    }
  }

  const routesToRender = new Set(filteredRoutes.map(r => r.route).filter(r => r !== undefined));

  if (routesOrderPreference) {
    const allowOverrideRoutes = app.prefs.get('app.routing.allowOverrideRoutes') || 'allowOverride';
    const routesOrderString = (allowOverrideRoutes === 'allowOverride') ? app.prefs.get(routesOrderPreference) : app.prefs.get(routesOrderPreference, 'company');

    if (routesOrderString) {
      const routesOrder = routesOrderString.split(',');
      if (routesOrder?.length > 1) {
        const orderedRoutes = [];
        const addRoute = route => {
          const foundRoute = filteredRoutes.find(r => r.route === route);
          if (foundRoute && !orderedRoutes.includes(foundRoute)) {
            orderedRoutes.push(foundRoute);
          }
        };
        const collapsedRoute = filteredRoutes.find(r => r.isCollapsedTitle);
        collapsedRoute && orderedRoutes.push(collapsedRoute);
        for (const route of routesOrder) {
          const routeKey = route.replace(/^!/, '');
          const foundRoute = filteredRoutes.find(r => r.route === routeKey);
          if (foundRoute?.alwaysAccessible) {
            continue; // opted-in routes ignore stale/explicit menu-order preferences and stay routable
          }
          if (route.charAt(0) !== '!') {
            addRoute(route);
          }
          routesToRender.delete(routeKey);
        }
        if (routesToRender.size > 0) {
          routesToRender.forEach(addRoute);
        }
        if (orderedRoutes.length >= 1) {
          filteredRoutes = orderedRoutes;
        }
      }
    }
  }

  return filteredRoutes;
}

let uniqueKey = 0;

/* eslint-disable max-statements */
function buildConfigRoutes(parentPath, routes, parentItem, options) {

  const {
    onlyStatic = false,
    withActions = false,
    withInjections = false,
    forcePath = false,
    withBreadcrumbName = false,
    withCollapsedTitles = false,
    withFirstChildPath = false
  } = options;

  const items = [];

  for (const route of routes) {

    const path = route.route || '';

    if (onlyStatic && !route.isStatic) {
      const isDynamic = path.indexOf(':') >= 0;
      if (isDynamic || !route.title) {
        continue;
      }
    }

    const absolutePath = path[0] === '/' ? path : parentPath + (path.length === 0 ? path : '/' + path);

    // NB! any additional fields will broke ProLayout
    const item = {
      icon: route.icon,
      name: route.title
    };

    if (route.isBeta) {
      item.isBeta = route.isBeta;
    }

    // A single `retiredDate` on a route drives hiding it from every menu/tab list and cutting
    // it off from inherited menuActions/entitySelector (see overrideActions below) — once past
    // that date, so route authors only ever need to set this one field.
    const isRetired = route.retiredDate && isPastRetirement(route.retiredDate);

    if (route.hidden || isRetired) {
      item.hidden = true;
    }

    if (route.isOld) {
      item.isOld = route.isOld;
    }

    if (route.isNew) {
      item.isNew = route.isNew;
    }

    if (route.dataTestId) {
      item.dataTestId = route.dataTestId;
    }

    if (route.isCollapsedTitle) {
      if (withCollapsedTitles) {
        item.isCollapsedTitle = true;
      } else {
        continue;
      }
    }

    if (options.withParent) {
      item.parent = parentItem;
    }

    if (withBreadcrumbName) {
      item.breadcrumbName = uniqueKey++;
    }

    assignRouteFields(item, route, options);

    if ('overrideActions' in route || isRetired) {
      item.overrideActions = isRetired || route.overrideActions;
    }

    if (withActions && route.menuActions) {
      item.menuActions = route.menuActions.filter(action => hasAccessTo(action));
    }

    if (withInjections && route.injections) {
      item.injections = route.injections.filter(action => hasAccessTo(action));
    }

    if (route.routes && route.routes.length > 0) {
      item.children = buildConfigRoutes(absolutePath, route.routes, item, options);
    }

    if (withFirstChildPath) {
      let firstChildPath = null;
      let current = item;
      while (current.children?.length > 0 && !firstChildPath) {
        let firstChild = current.children[0];

        if (firstChild.isCollapsedTitle) {
          firstChild = current.children[1];
        }

        if (!firstChild) {
          break;
        }

        firstChildPath = firstChild.path;
        current = firstChild;
      }
      item.firstChildPath = firstChildPath;
    }

    if (forcePath || !route.routes || route.routes.length === 0) {
      item.path = absolutePath;
    }

    items.push(item);
  }

  return items;
}
/* eslint-enable max-statements */

function assignRouteFields(target, route, options) {

  if (options.withHideContentMenu && route.hideContentMenu) {
    target.hideContentMenu = route.hideContentMenu;
  }

  if (options.withContextMenu && route.contextMenu) {
    target.contextMenu = route.contextMenu;
  }

  if (options.withEntitySelector && route.entitySelector) {
    target.entitySelector = route.entitySelector;
  }

}

export function buildRoutes(parentPath, routes, options = {}) {
  let items = buildConfigRoutes(parentPath, routes, undefined, options);
  if (options.withCollapsedTitles) {
    // checking only first lever for now
    // ignoring routes only with collapsedTitles
    items = items.filter(i => (i.children && i.children.length > 1) || (i.path && i.path.indexOf('analytics') >= 0));
  }

  return items;
}

export function findFlatRouteByPathname(routes = [], pathname) {

  for (const route of sortRoutes(routes)) {
    const match = matchPath(pathname, { path: route.path });
    if (match) {
      return route;
    }
  }

  return null;
}

export function matchRouteByPathname(routes = [], pathname) {

  const countMatchingSegments = (routePath, pathname) => {
    if (!routePath) {
 return 0;
}

    const routeSegments = routePath.split('/').filter(Boolean);
    const pathSegments = pathname.split('/').filter(Boolean);

    // Use reduce to count matching segments
    return routeSegments.reduce((count, segment, index) => {
      if (segment === pathSegments[index] || segment.startsWith(':')) {
        return count + 1; // Increment match count
      }
      return count; // Stop counting on first mismatch
    }, 0);
  };

  const matchRoute = (routes, matchedRoutes, numberOfMatchingSegmentsMap) => {

    for (const route of routes) {
      const match = matchPath(pathname, { path: route.path });
      if (route.children) {
        matchRoute(route.children, matchedRoutes, numberOfMatchingSegmentsMap);
      }

      if (match) {
        const matchingSegments = countMatchingSegments(route.path, pathname);
        numberOfMatchingSegmentsMap.set(route.path, matchingSegments);
        matchedRoutes.push([route, match]);
      }
    }
  };

  const matchedRoutes = [];
  const numberOfMatchingSegmentsMap = new Map();

  matchRoute(routes, matchedRoutes, numberOfMatchingSegmentsMap);

  if (matchedRoutes.length === 0) {
    return [];
  }

  matchedRoutes.sort(([a], [b]) => {

    const pathA = a.path || '';
    const pathB = b.path || '';
    const aMatchingSegments = numberOfMatchingSegmentsMap.get(pathA) || 0;
    const bMatchingSegments = numberOfMatchingSegmentsMap.get(pathB) || 0;
    // Number of matching segments (descending)
    if (bMatchingSegments !== aMatchingSegments) {
      return bMatchingSegments - aMatchingSegments;
    }

    // Secondary: Static paths before dynamic paths
    const aDynamicCount = (pathA.match(/:/g) || []).length;
    const bDynamicCount = (pathB.match(/:/g) || []).length;
    if (aDynamicCount !== bDynamicCount) {
      return aDynamicCount - bDynamicCount; // fewer dynamic segments wins
    }
    if (pathA === pathB) {
      return a.children ? 1 : -1;
    }
    return 0;
  });

  return matchedRoutes[0] || [];
}

function sortRoutes(routes = []) {
  const sorted = [...routes];
  sorted.sort((a, b) => b.path.length - a.path.length);
  return sorted;
}

export function routesToArray(routes, previousPath, routeNodes) {

  for (const routeNode of routeNodes) {
    const regex = /[^/]+\/$/;
    const notAddSlash = regex.test(previousPath);
    const path = notAddSlash ? `${previousPath}${routeNode.route}` : `${previousPath}/${routeNode.route}`;

    // A route declaring `retiredDate` gets forwarded to the common sunset page (PFM-1426) once
    // that date has passed, regardless of what `component` it normally renders (or even if it
    // has none, e.g. a legacy-only page).
    const component = (routeNode.retiredDate && isPastRetirement(routeNode.retiredDate)) ? RedirectToSunset : routeNode.component;

    if (component) {

      const route = {
        path,
        component,
        title: routeNode.title,
        emptyRoute: routeNode.route.length === 0
      };

      // HACK in case of menu items where route should be '', can be used /entries route
      // WHY? when route is "sub"-route - highlighting of correct active menu item not working in ant layout
      // /customers menu item will be highlighted when /customers/anyPage selected
      // setting up { exact: true } in routes not working (tested up to 6.24.2 version)
      if (path.endsWith('/entries')) {
        routes.push({
          ...route,
          path: path.slice(0, -'/entries'.length)
        });
      }

      routes.push(route);
    }
    if (routeNode.routes) {
      routesToArray(routes, path, routeNode.routes);
    }
  }
}

export function filterRoutesInto(result = [], routes = [], check) {

  for (const route of routes) {

    if (check(route)) {
      result.push(route);
    }
    if (route.children) {
      filterRoutesInto(result, route.children, check);
    }
  }
}

export function redirectToLogin(redirectPathAfterLogin) {
  window.location.href = Navigate.link.toLogin(redirectPathAfterLogin);
}

export function redirectToLogout() {
  window.location.assign(Navigate.link.toLogout());
}

export function redirectToSignup() {
  window.location.assign(Navigate.link.toSignup());
}

export function redirectToSuccessCreateCompany() {
  window.location.assign(Navigate.link.toSuccessCreateCompany());
}

export function resolveUrlParams(pathname, path) {

  const lastParamSlash = path.indexOf('/', path.lastIndexOf(':') + 1);
  let paramsPath = path.substr(0, lastParamSlash === -1 ? undefined : lastParamSlash);

  // HACK to support tibet navigation in travel and expenses
  if (paramsPath.indexOf('/claims/:overview') > -1) {
    paramsPath = paramsPath.replace('/claims/:overview', '');
  }

  const match = matchPath(pathname, { path: paramsPath });

  let newPath = path;
  if (match && match.params) {
    for (const key in match.params) {
      const value = match.params[key];
      if (!value) {
        newPath = newPath.replace('/:' + key + '?', '');
      }
      newPath = newPath.replace(':' + key + '?', value);
      newPath = newPath.replace(':' + key, value);
    }
  }

  return newPath;
}


export function resolveComplexRoutePath(route) {

  if (route.children?.length > 0) {
    return route.children[0].path || resolveComplexRoutePath(route.children[0]);
  }

  return route.path;
}

// backbone fallback TODO rewrite to store state on react side (mobx?)
export function canLeaveCurrentView() {
  return window.tb.app.navigation.events.unconfirmedChanges.length > 0;
}

export function cleanUnconfirmedChanges() {
  return window.tb.app.navigation.events.unconfirmedChanges = [];
}

// Wrapper to ensure we're not rendering component even when the toggle button is not shown
export const withPreference = (Component, { requirePreference }, FallbackComponent = () => null) => {
  return (props) => {
    const preferences = usePreferences();
    const hasPreference = !requirePreference || preferences[requirePreference] === 'true';
    return hasPreference ? <Component {...props} /> : <FallbackComponent {...props} />;
  };
};

export const MenuItemLabel = ({ isBeta, isOld, isNew }) => {
  const style = { marginLeft: '1ex' };
  if (isBeta) {
    return <Tag color="red" style={style}>{t('menu.betaLabel')}</Tag>;
  }

  if (isOld) {
    return <Tag style={style}>{t('menu.oldLabel')}</Tag>;
  }

  if (isNew) {
    return <Badge count={<DuotoneAnimatedIcon icon={faSparkles} style={{
      transform: 'rotate(30deg)',
      color: 'var(--color-primary)',
      fontSize: '15px',
      width: 'fit-content'
    }} />}>
      <Tag style={{ ...style, color: 'var(--color-text)' }}>{t('menu.newLabel')}</Tag>
    </Badge>;
  }

  return null;
};

MenuItemLabel.propTypes = {
  isBeta: PropTypes.bool,
  isOld: PropTypes.bool,
  isNew: PropTypes.bool
};
